import { type ReactNode } from 'react'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  Sparkles, 
  Goal, 
  Spotlight, 
  Users, 
  Fingerprint,
  Clock,
  CheckCircle
} from 'lucide-react'

import { OnboardingScenario } from '@/types/onboarding'

interface WelcomeStepProps {
  scenario: OnboardingScenario
  employeeName: string
  jobTitle: string
  onNext: () => void
  estimatedDuration: number
}

export default function WelcomeStep({
  scenario,
  employeeName,
  jobTitle,
  onNext,
  estimatedDuration
}: WelcomeStepProps) {
  
  const getScenarioContent = () => {
    switch (scenario) {
      case 'first-time-user':
        return {
          title: <>Welcome to the (impact <span className="text-[0.75em]">&</span> growth)<sup className="text-[1em] relative -top-[0.35em] font-extrabold">+</sup> platform</>,
          description: 'We\'re excited to have you on board! This quick setup will personalise your experience and help you get the most out of your career journey.',
          badge: { text: 'First Time Setup', variant: 'default' as const }
        }
      case 'role-change':
        return {
          title: 'Welcome to Your New Role!',
          subtitle: 'Let\'s update your profile',
          description: 'Congratulations on your new position! We need to update a few details to align your impact & growth journey with your new responsibilities.',
          badge: { text: 'Role Change', variant: 'secondary' as const }
        }
      case 'annual-profile-update':
        return {
          title: 'Annual Profile Update',
          subtitle: 'Time to refresh your professional profile',
          description: 'Keeping your profile current helps your people leader prepare for meaningful career and development conversations with you.',
          badge: { text: 'Annual Update', variant: 'outline' as const }
        }
      case 'campus-transfer':
        return {
          title: 'Welcome to Your New Campus!',
          subtitle: 'Let\'s update your campus information',
          description: 'We\'ve detected a campus transfer in your F&O records. Let\'s ensure your profile reflects your new location.',
          badge: { text: 'Campus Transfer', variant: 'secondary' as const }
        }
    }
  }

  const content = getScenarioContent()

  const features = [
    {
      icon: Goal,
      title: 'Objective Alignment & Progress',
      description: 'set objectives & track outcomes'
    },
    {
      icon: Spotlight,
      title: 'Impact Portfolio',
      description: 'showcase outcomes & impact'
    },
    {
      icon: Users,
      title: 'Reflections & Feedback',
      description: 'meaningfully engage via quarterly 1:1 check-in dialogues as well as ad-hoc feedback requests of anyone at anytime'
    },
    {
      icon: Fingerprint,
      title: 'Personalised Growth',
      description: 'curate a growth journey of mindsets, skills and behaviours unique to role and personal desires -- there could be as many journeys as we have people!'
    }
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-4"
      >
        <div className="flex justify-center mb-6">
          <img src="/assets/logos/Light-Logo-Impact-Growth2.png" alt="My Impact & Growth Journey" className="h-44 object-contain" />
        </div>
        
        <Badge className="mb-4 bg-heritage-blue text-white">{content.badge.text}</Badge>
        
        <h1 className="text-4xl font-primary font-bold text-heritage-blue">
          {content.title}
        </h1>
        <p className="text-xl text-muted-foreground">
          {content.subtitle}
        </p>
      </motion.div>

      {/* Welcome Message */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="bg-gradient-to-br from-heritage-blue/5 to-future-blue/5 border border-heritage-blue/20 rounded-xl p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-heritage-blue/20 to-future-blue/20 flex items-center justify-center">
                <Sparkles className="h-7 w-7 text-heritage-blue" />
              </div>
            </div>
            <div className="flex-1 space-y-2">
              <h3 className="text-xl font-semibold text-heritage-blue">
                Hello, {employeeName}!
              </h3>
              <p className="text-sm text-muted-foreground font-medium">
                {jobTitle}
              </p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {content.description}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Features Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        {features.map((feature, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 + index * 0.05 }}
            className="rounded-xl p-5"
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-heritage-blue/40 via-heritage-blue/20 to-heritage-blue/5 backdrop-blur-md rounded-xl flex items-center justify-center shadow-[0_8px_24px_rgba(0,0,0,0.12)] border border-heritage-blue/20 ring-1 ring-heritage-blue/10" style={{ transform: 'perspective(400px) rotateY(-20deg) rotateX(20deg)' }}>
                <feature.icon className="w-6 h-6 text-heritage-blue drop-shadow-[0_2px_4px_rgba(0,0,0,0.15)]" />
              </div>
              <div>
                <h4 className="font-semibold text-sm text-heritage-blue">{feature.title}</h4>
                <p className="text-xs text-muted-foreground mt-1">{feature.description}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Timeline */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <div className="border border-white/50 rounded-xl p-6 bg-white/50">
            <div className="flex items-center gap-3 mb-6">
              <h3 className="font-semibold text-lg text-heritage-blue">What to Expect</h3>
              <Badge className="bg-future-blue/10 text-future-blue border-future-blue/20">
                ~{estimatedDuration} minutes
              </Badge>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-heritage-blue">Confirm your profile data</p>
                  <p className="text-xs text-muted-foreground">Verify your role, campus and reporting line</p>
                </div>
              </div>
              
              {scenario === 'first-time-user' && (
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-heritage-blue">Tell us about your background</p>
                    <p className="text-xs text-muted-foreground">Add qualifications, career history and areas of expertise</p>
                  </div>
                </div>
              )}
              
              {scenario === 'first-time-user' && (
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-heritage-blue">You're all set!</p>
                    <p className="text-xs text-muted-foreground">An interactive tour will guide you when you first open the platform</p>
                  </div>
                </div>
              )}
            </div>
        </div>
      </motion.div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="flex justify-center pt-6"
      >
        <Button 
          onClick={onNext}
          size="lg"
          className="bg-gradient-to-br from-heritage-blue to-future-blue hover:from-heritage-blue/90 hover:to-future-blue/90 text-white px-12 py-6 text-lg shadow-lg"
        >
          Get Started
          <Sparkles className="ml-2 h-5 w-5" />
        </Button>
      </motion.div>
    </div>
  )
}
